import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import {
  Instagram,
  Youtube,
  Globe,
  Image as ImageIcon,
  UtensilsCrossed,
  Music,
  Gift,
  Dumbbell,
  Users,
  Podcast,
  FileText,
  Bell,
  Search,
  CalendarDays,
  Newspaper,
  Plane,
  CalendarHeart,
  Pause,
  Play,
} from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFacebook, faLinkedinIn } from "@fortawesome/free-brands-svg-icons";
import { XLogo, TikTokLogo, RedditLogo } from "@/components/shared/PlatformIcon";
import { resolveGestureAxis, type GestureAxis } from "@/lib/scroll/gestureAxisLock";
import { LABEL } from "@/lib/typography";

type FloatingItem =
  | { type: "icon"; icon: React.ReactNode; label?: string; className: string; color?: string; textSize?: "xs" | "sm" }
  | { type: "text"; text: string; icon?: React.ReactNode; className: string; color?: string; textSize?: "xs" | "sm" };

export type HeroStep = {
  headingLine1: string;
  headingLine2: string;
  subtitleLine1: string;
  subtitleLine2: string;
  floating?: FloatingItem[];
} & (
  | { type: "single"; src: string; alt: string }
  | {
      type: "stacked";
      layers: Array<{
        src: string;
        alt: string;
        className: string;
        framed: boolean;
        width: number;
        height: number;
      }>;
    }
);

export const HERO_STEPS: HeroStep[] = [
  {
    type: "single",
    src: "/hero-step-0.webp",
    alt: "See a post you love on Instagram",
    headingLine1: "You save what",
    headingLine2: "you love.",
    subtitleLine1: "Whatever catches your eye that you",
    subtitleLine2: "want to remember.",
    floating: [
      {
        type: "text",
        text: "Restaurants",
        icon: <UtensilsCrossed size={14} />,
        className: "top-[5%] left-[1%] rotate-[-4deg]",
        color: "bg-orange-100 text-orange-700 border-orange-200",
      },
      {
        type: "text",
        text: "Concerts",
        icon: <Music size={14} />,
        className: "top-[8%] right-[6%] rotate-[3deg]",
        color: "bg-purple-100 text-purple-700 border-purple-200",
      },
      {
        type: "text",
        text: "Recipes",
        icon: <UtensilsCrossed size={14} />,
        className: "top-[20%] left-[4%] rotate-[2deg]",
        color: "bg-red-100 text-red-700 border-red-200",
      },
      {
        type: "text",
        text: "Events",
        icon: <CalendarHeart size={14} />,
        className: "top-[22%] right-[1%] rotate-[-4deg]",
        color: "bg-indigo-100 text-indigo-700 border-indigo-200",
      },
      {
        type: "text",
        text: "Travel",
        icon: <Plane size={14} />,
        className: "top-[38%] right-[4%] rotate-[2deg]",
        color: "bg-sky-100 text-sky-700 border-sky-200",
      },
      {
        type: "text",
        text: "Gift Ideas",
        icon: <Gift size={14} />,
        className: "top-[40%] left-[0%] rotate-[-3deg]",
        color: "bg-pink-100 text-pink-700 border-pink-200",
      },
      {
        type: "text",
        text: "Family",
        icon: <Users size={14} />,
        className: "bottom-[30%] left-[3%] rotate-[3deg]",
        color: "bg-teal-100 text-teal-700 border-teal-200",
      },
      {
        type: "text",
        text: "Fitness",
        icon: <Dumbbell size={14} />,
        className: "bottom-[32%] right-[1%] rotate-[-3deg]",
        color: "bg-green-100 text-green-700 border-green-200",
      },
      {
        type: "text",
        text: "Articles",
        icon: <FileText size={14} />,
        className: "bottom-[18%] left-[1%] rotate-[-2deg]",
        color: "bg-amber-100 text-amber-700 border-amber-200",
      },
      {
        type: "text",
        text: "Podcasts",
        icon: <Podcast size={14} />,
        className: "bottom-[20%] right-[5%] rotate-[2deg]",
        color: "bg-violet-100 text-violet-700 border-violet-200",
      },
      { type: "text", text: "and more", className: "bottom-[3%] right-[2%] rotate-[-2deg]" },
    ],
  },
  {
    type: "single",
    src: "/hero-step-1.webp",
    alt: "Share it to Stasht from the iOS share sheet",
    headingLine1: "From anywhere,",
    headingLine2: "all in one place.",
    subtitleLine1: "No more saves and screenshots",
    subtitleLine2: "scattered across ten apps.",
    floating: [
      {
        type: "icon",
        icon: <Instagram size={22} />,
        className: "top-[8%] left-[8%] rotate-[-5deg]",
        color: "bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 text-white border-pink-400",
      },
      {
        type: "icon",
        icon: <TikTokLogo size={22} />,
        className: "top-[4%] right-[14%] rotate-[4deg]",
        color: "bg-black text-white border-gray-700",
      },
      {
        type: "icon",
        icon: <XLogo size={22} />,
        className: "top-[22%] right-[8%] rotate-[-3deg]",
        color: "bg-black text-white border-gray-700",
      },
      {
        type: "icon",
        icon: <FontAwesomeIcon icon={faFacebook} style={{ width: 22, height: 22 }} />,
        className: "top-[38%] left-[6%] rotate-[3deg]",
        color: "bg-[#1877F2] text-white border-blue-500",
      },
      {
        type: "icon",
        icon: <Youtube size={22} />,
        className: "bottom-[34%] right-[8%] rotate-[-4deg]",
        color: "bg-[#FF0000] text-white border-red-500",
      },
      {
        type: "icon",
        icon: <RedditLogo size={22} />,
        className: "bottom-[24%] left-[6%] rotate-[5deg]",
        color: "bg-[#FF4500] text-white border-orange-500",
      },
      {
        type: "icon",
        icon: <FontAwesomeIcon icon={faLinkedinIn} style={{ width: 22, height: 22 }} />,
        className: "bottom-[16%] right-[10%] rotate-[-3deg]",
        color: "bg-[#0A66C2] text-white border-blue-600",
      },
      {
        type: "icon",
        icon: <ImageIcon size={14} />,
        label: "Screenshots",
        className: "bottom-[8%] left-[1%] rotate-[2deg]",
      },
      {
        type: "icon",
        icon: <Globe size={14} />,
        label: "Websites",
        className: "bottom-[4%] right-[1%] rotate-[-2deg]",
      },
    ],
  },
  {
    type: "stacked",
    headingLine1: "We make them",
    headingLine2: "better.",
    subtitleLine1: "Everything you stash gets richer",
    subtitleLine2: "the moment it lands.",
    floating: [
      { type: "text", text: "every detail pulled out", className: "top-0 left-[1%] rotate-[-3deg]" },
      { type: "text", text: "automatically organized", className: "bottom-0 right-[1%] rotate-[3deg]" },
    ],
    layers: [
      {
        src: "/homepage/coffee-detail.webp",
        alt: "A coffee post saved in Stasht",
        className: "max-w-[43%] max-h-[75%] left-[5%] top-[12%] rotate-[-4deg]",
        framed: true,
        width: 1179,
        height: 2556,
      },
      {
        src: "/homepage/places-detail.webp",
        alt: "Saved place details and a map in Stasht",
        className: "max-w-[43%] max-h-[75%] right-[5%] top-[14%] rotate-[4deg]",
        framed: true,
        width: 1206,
        height: 2622,
      },
    ],
  },
  {
    type: "stacked",
    headingLine1: "And bring it back",
    headingLine2: "when it matters.",
    subtitleLine1: "Restaurants on your map when you\u2019re nearby.",
    subtitleLine2: "Concerts on your calendar before tickets sell out.",
    floating: [
      {
        type: "text",
        text: "Calendar",
        icon: <CalendarDays size={16} />,
        className: "top-0 left-[2%] rotate-[-4deg]",
        textSize: "sm",
      },
      {
        type: "text",
        text: "Search",
        icon: <Search size={16} />,
        className: "top-0 right-[2%] rotate-[2deg]",
        textSize: "sm",
      },
      {
        type: "text",
        text: "Weekly Roundups",
        icon: <Newspaper size={16} />,
        className: "bottom-0 left-[2%] rotate-[2deg]",
        textSize: "sm",
      },
      {
        type: "text",
        text: "Alerts",
        icon: <Bell size={16} />,
        className: "bottom-0 right-[2%] rotate-[-3deg]",
        textSize: "sm",
      },
    ],
    layers: [
      {
        src: "/homepage/saved-places-map.webp",
        alt: "Saved places shown together on the Stasht map",
        className: "max-w-[43%] max-h-[75%] left-[5%] top-[12%] rotate-[-4deg]",
        framed: true,
        width: 1206,
        height: 2622,
      },
      {
        src: "/homepage/nearby-map.webp",
        alt: "Nearby saved places on the Stasht map",
        className: "max-w-[43%] max-h-[75%] right-[5%] top-[14%] rotate-[4deg]",
        framed: true,
        width: 945,
        height: 2048,
      },
    ],
  },
];

export const STEP_DURATION = 8000;
const SWIPE_THRESHOLD = 25;
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

const subscribeToReducedMotion = (onChange: () => void) => {
  const query = window.matchMedia(REDUCED_MOTION_QUERY);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
};
const getReducedMotion = () => window.matchMedia(REDUCED_MOTION_QUERY).matches;
const getServerReducedMotion = () => true;

export const TOUCH_PASSTHROUGH = "pointer-events-none [&_a]:pointer-events-auto [&_button]:pointer-events-auto";

export const StepIndicators = ({
  active,
  total,
  goTo,
  paused,
  onPause,
  onTogglePlayback,
  reducedMotion = false,
}: {
  active: number;
  total: number;
  goTo: (i: number) => void;
  paused: boolean;
  onPause?: () => void;
  onTogglePlayback?: () => void;
  reducedMotion?: boolean;
}) => (
  <div className="flex justify-center gap-1" role="group" aria-label="Slideshow controls">
    {onTogglePlayback && (
      <button
        type="button"
        aria-label={
          reducedMotion ? "Automatic playback disabled for reduced motion" : paused ? "Play slideshow" : "Pause slideshow"
        }
        title={
          reducedMotion ? "Automatic playback disabled for reduced motion" : paused ? "Play slideshow" : "Pause slideshow"
        }
        disabled={reducedMotion}
        onFocus={event => {
          // Pointer focus must not change the action just before its click.
          if (event.currentTarget.matches(":focus-visible")) onPause?.();
        }}
        onClick={onTogglePlayback}
        className="flex h-[44px] w-[44px] items-center justify-center rounded-full text-foreground/70 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-40 motion-reduce:transition-none"
      >
        {paused ? (
          <Play className="h-4 w-4" aria-hidden="true" />
        ) : (
          <Pause className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
    )}
    {Array.from({ length: total }, (_, i) => (
      <button
        key={i}
        type="button"
        aria-label={`Step ${i + 1} of ${total}`}
        aria-current={i === active ? "step" : undefined}
        onFocus={onPause}
        onClick={() => goTo(i)}
        className="relative flex h-[44px] w-[44px] cursor-pointer items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 motion-reduce:transition-none"
      >
        <span
          aria-hidden="true"
          className={`relative block h-2.5 overflow-hidden rounded-full transition-all duration-500 ease-out motion-reduce:transition-none ${
            i === active ? "bg-brand-500/20" : "bg-foreground/15 dark:bg-foreground/40"
          }`}
          style={{ width: i === active ? 36 : 10 }}
        >
          {i === active && (
            <span
              className="absolute inset-y-0 left-0 w-full origin-left rounded-full bg-brand-500"
              style={{
                animation: paused || reducedMotion ? "none" : `hero-progress ${STEP_DURATION}ms linear forwards`,
              }}
            />
          )}
        </span>
      </button>
    ))}
  </div>
);

export type HeroPlaybackState = { paused: boolean; manuallyPaused: boolean; reducedMotion: boolean };
export type HeroSequenceHandle = HeroPlaybackState & {
  goTo: (i: number, stopAuto?: boolean) => void;
  pause: () => void;
  resume: () => void;
};

export const HeroSequence = ({
  onStepChange,
  onPausedChange,
  onPlaybackStateChange,
  controlsRef,
}: {
  onStepChange?: (step: number) => void;
  onPausedChange?: (paused: boolean) => void;
  onPlaybackStateChange?: (state: HeroPlaybackState) => void;
  controlsRef?: React.MutableRefObject<HeroSequenceHandle | null>;
}) => {
  const [active, setActive] = useState(0);
  const [prev, setPrev] = useState(-1);
  const [hovered, setHovered] = useState(false);
  const [manuallyPaused, setManuallyPaused] = useState(false);
  const reducedMotion = useSyncExternalStore(subscribeToReducedMotion, getReducedMotion, getServerReducedMotion);
  const paused = hovered || manuallyPaused || reducedMotion;
  const activeRef = useRef(active);
  const pause = useCallback(() => setManuallyPaused(true), []);
  const resume = useCallback(() => setManuallyPaused(false), []);

  const goTo = useCallback(
    (next: number, stopAuto?: boolean) => {
      if (stopAuto) pause();
      const nextIndex = ((next % HERO_STEPS.length) + HERO_STEPS.length) % HERO_STEPS.length;
      const current = activeRef.current;
      if (nextIndex === current) return;
      activeRef.current = nextIndex;
      setPrev(current);
      setActive(nextIndex);
      onStepChange?.(nextIndex);
    },
    [onStepChange, pause],
  );

  useEffect(() => {
    if (!controlsRef) return;
    controlsRef.current = { goTo, paused, manuallyPaused, reducedMotion, pause, resume };
    return () => {
      controlsRef.current = null;
    };
  }, [controlsRef, goTo, paused, manuallyPaused, reducedMotion, pause, resume]);

  useEffect(() => {
    onPausedChange?.(paused);
  }, [onPausedChange, paused]);

  useEffect(() => {
    onPlaybackStateChange?.({ paused, manuallyPaused, reducedMotion });
  }, [onPlaybackStateChange, paused, manuallyPaused, reducedMotion]);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      goTo((activeRef.current + 1) % HERO_STEPS.length);
    }, STEP_DURATION);
    return () => clearInterval(id);
  }, [goTo, paused]);

  const touchStart = useRef<{ x: number; y: number; identifier: number; axis: GestureAxis } | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length !== 1) {
      touchStart.current = null;
      return;
    }
    const touch = e.touches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY, identifier: touch.identifier, axis: "undecided" };
  }, []);

  const clearTouch = useCallback(() => {
    touchStart.current = null;
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const start = touchStart.current;
      if (!start) return;
      if (e.touches.length !== 1 || e.touches[0].identifier !== start.identifier) {
        clearTouch();
        return;
      }
      if (start.axis === "vertical") return;
      const deltaX = start.x - e.touches[0].clientX;
      const deltaY = start.y - e.touches[0].clientY;
      start.axis = resolveGestureAxis(deltaX, deltaY);
      if (start.axis === "horizontal" && Math.abs(deltaX) >= SWIPE_THRESHOLD) {
        clearTouch();
        goTo(activeRef.current + (deltaX > 0 ? 1 : -1), true);
      }
    },
    [goTo, clearTouch],
  );

  const getGroupStyle = (i: number): React.CSSProperties => {
    const isActive = i === active;
    const isPrev = i === prev;

    if (reducedMotion) {
      return { opacity: isActive ? 1 : 0, transform: "none", zIndex: isActive ? 2 : 0, transition: "none" };
    }

    if (isActive) {
      return {
        opacity: 1,
        transform: "scale(1) translateY(0)",
        zIndex: 2,
        transition:
          "opacity 500ms 200ms cubic-bezier(0.4, 0, 0.2, 1), transform 500ms 200ms cubic-bezier(0.4, 0, 0.2, 1)",
      };
    }
    if (isPrev) {
      return {
        opacity: 0,
        transform: "scale(0.95) translateY(8px)",
        zIndex: 1,
        transition: "opacity 400ms cubic-bezier(0.4, 0, 0.2, 1), transform 400ms cubic-bezier(0.4, 0, 0.2, 1)",
      };
    }
    return {
      opacity: 0,
      transform: "scale(1.03) translateY(-8px)",
      zIndex: 0,
      transition: "none",
    };
  };

  return (
    <div
      className="relative w-full h-full z-10 touch-pan-y touch-pinch-zoom"
      role="group"
      aria-label="How Stasht works"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={clearTouch}
      onTouchCancel={clearTouch}
    >
      {HERO_STEPS.map((step, i) => (
        <div
          key={i}
          role="group"
          aria-roledescription="slide"
          aria-label={`${i + 1} of ${HERO_STEPS.length}: ${step.headingLine1} ${step.headingLine2}`}
          aria-hidden={i !== active}
          className="absolute inset-0 w-full h-full will-change-[opacity,transform]"
          style={getGroupStyle(i)}
        >
          {step.type === "single" ? (
            <img
              alt={step.alt}
              src={step.src}
              loading={i === 0 ? "eager" : "lazy"}
              fetchPriority={i === 0 ? "high" : undefined}
              className="w-full h-full object-contain pointer-events-none"
            />
          ) : (
            step.layers.map((layer, li) => (
              <img
                key={layer.src}
                alt={layer.alt}
                src={layer.src}
                loading="lazy"
                width={layer.width}
                height={layer.height}
                className={`absolute h-auto w-auto object-contain pointer-events-none ${
                  layer.framed
                    ? "rounded-[clamp(12px,2vw,24px)] border-[3px] border-neutral-950 bg-neutral-950 shadow-2xl"
                    : "drop-shadow-2xl"
                } ${layer.className}`}
                style={{
                  zIndex: li,
                  opacity: i === active ? 1 : 0,
                  transform: reducedMotion
                    ? "none"
                    : i === active
                      ? "translateY(0) scale(1)"
                      : "translateY(20px) scale(0.95)",
                  transition:
                    i === active && !reducedMotion
                      ? `opacity 500ms ${400 + li * 300}ms cubic-bezier(0.4,0,0.2,1), transform 500ms ${400 + li * 300}ms cubic-bezier(0.4,0,0.2,1)`
                      : "none",
                }}
              />
            ))
          )}
          {step.floating?.map((item, fi) => (
            <div
              key={fi}
              className={`absolute z-10 ${item.className}`}
              style={{
                opacity: i === active ? 1 : 0,
                transform: reducedMotion ? "none" : i === active ? "scale(1) translateY(0)" : "scale(0.8) translateY(8px)",
                transition:
                  i === active && !reducedMotion
                    ? `opacity 400ms ${800 + fi * 100}ms cubic-bezier(0.4,0,0.2,1), transform 400ms ${800 + fi * 100}ms cubic-bezier(0.4,0,0.2,1)`
                    : "none",
              }}
            >
              {item.type === "icon" ? (
                item.label ? (
                  <span
                    className={`flex items-center gap-1.5 rounded-full backdrop-blur-sm border px-3 py-1.5 shadow-lg ${
                      item.color ??
                      "bg-background/90 dark:bg-white/15 border-foreground/10 dark:border-white/20 text-foreground"
                    }`}
                  >
                    {item.icon}
                    <span className={LABEL}>{item.label}</span>
                  </span>
                ) : (
                  <span
                    className={`flex items-center justify-center rounded-full backdrop-blur-sm border p-2.5 shadow-lg ${
                      item.color ??
                      "bg-background/90 dark:bg-white/15 border-foreground/10 dark:border-white/20 text-foreground"
                    }`}
                  >
                    {item.icon}
                  </span>
                )
              ) : (
                <span
                  className={`flex items-center gap-1.5 rounded-full backdrop-blur-sm border px-3 py-1.5 shadow-lg font-semibold whitespace-nowrap ${
                    item.textSize === "sm" ? "text-ui-control" : "text-ui-caption"
                  } ${item.color ?? "bg-background/90 dark:bg-white/15 border-foreground/10 dark:border-white/20 text-foreground"}`}
                >
                  {item.icon}
                  {item.text}
                </span>
              )}
            </div>
          ))}
        </div>
      ))}
      <style>{`
        @keyframes hero-progress {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
        @keyframes subtitle-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes subtitle-in-delayed {
          0%   { opacity: 0; transform: translateY(6px); }
          40%  { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};
